import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

class StickyAddToCart extends HTMLElement {
  #abortController = new AbortController();
  /** @type {IntersectionObserver | null} */
  #visibilityObserver = null;

  connectedCallback() {
    const section = this.closest('.shopify-section');
    if (!section) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    section.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    section.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });
    section.addEventListener('subscription:update', this.#handleSubscriptionUpdate, { signal });
    section.addEventListener('product:switched', this.#handleProductSwitched, { signal });
    window.addEventListener('resize', this.#handleViewportResize, { signal });

    this.#syncQuantityFromMain();
    this.#syncPriceFromSubscription();
    this.#setupVisibilityObserver();
  }

  /**
   * When product-section-switcher morphs in a new product, the element we were observing
   * (the main primary purchase actions) is replaced. Re-point the IntersectionObserver
   * at the new node so sticky visibility keeps working after a flavour/size switch.
   */
  #handleProductSwitched = () => {
    this.#syncQuantityFromMain();
    this.#syncPriceFromSubscription();
    this.#setupVisibilityObserver();
  };

  disconnectedCallback() {
    this.#abortController.abort();
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
  }

  /**
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {import('@theme/events').VariantUpdateEvent} */ (event).detail;
    const eventProductId = detail?.data?.newProduct?.id || detail?.data?.productId;
    const currentProductId = this.dataset.productId;

    if (!eventProductId) return;
    if (currentProductId && eventProductId !== currentProductId && !detail?.data?.newProduct) return;

    const newSticky = detail?.data?.html?.querySelector('sticky-add-to-cart');
    if (newSticky) {
      morph(this, newSticky);
      this.#syncQuantityFromMain();
      this.#syncPriceFromSubscription();
      this.#setupVisibilityObserver();
      return;
    }

    const imageSrc = detail?.resource?.featured_media?.preview_image?.src;
    const imageWrapper = this.querySelector('[data-sticky-add-to-cart-image]');
    const image =
      imageWrapper instanceof HTMLImageElement ? imageWrapper : imageWrapper?.querySelector('img');
    if (imageSrc && image instanceof HTMLImageElement) {
      const width = imageWrapper?.dataset.width || image.dataset.width || '120';
      const width2x = imageWrapper?.dataset.width2x || image.dataset.width2x || '240';
      image.src = `${imageSrc}&width=${width}`;
      image.srcset = `${imageSrc}&width=${width2x} 2x`;
    }
  };

  /**
   * @param {Event} event
   */
  #handleQuantityUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (this.contains(event.target)) return;

    const section = this.closest('.shopify-section');
    if (section && !section.contains(event.target)) return;

    const productForm = event.target.closest('product-form-component');
    if (
      productForm instanceof HTMLElement &&
      this.dataset.productId &&
      productForm.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const quantity = /** @type {any} */ (event).detail?.quantity;
    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    if (stickyQuantityInput instanceof HTMLInputElement) {
      stickyQuantityInput.value = quantity?.toString() || stickyQuantityInput.value;
    }
  };

  #syncQuantityFromMain() {
    if (!this.#hasStickyProductForm()) return;

    const stickyQuantityInput = this.querySelector('[data-sticky-quantity]');
    if (!(stickyQuantityInput instanceof HTMLInputElement)) return;

    const mainInput = this.#getMainQuantityInput();
    if (!mainInput) return;

    stickyQuantityInput.value = mainInput.value;
  }

  /**
   * Imperatively syncs subscription state on initial load.
   * Needed because the sticky bar may connect after the subscription block
   * has already fired its initial subscription:update event.
   */
  #syncPriceFromSubscription() {
    if (!this.#hasStickyProductForm()) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    const subscription = section.querySelector('product-subscription');
    if (!(subscription instanceof HTMLElement)) return;

    const modeInput = subscription.querySelector('input[data-subscription-mode]:checked');
    const mode =
      (modeInput instanceof HTMLInputElement && modeInput.value) || subscription.dataset.subscriptionMode || 'one-time';

    // Price is rendered server-side in both one-time and subscription sets.
    // Just toggle which set is visible.
    const productPrice = this.querySelector('product-price');
    if (productPrice && typeof productPrice.setMode === 'function') {
      productPrice.setMode(mode);
    }

    // Sync selling plan input and button text
    const sellingPlanInput = this.querySelector('[data-sticky-selling-plan]');
    if (sellingPlanInput instanceof HTMLInputElement) {
      if (mode === 'subscription') {
        const select = subscription.querySelector('[data-subscription-select]');
        const planId =
          (select instanceof HTMLSelectElement && select.value) || subscription.dataset.defaultSellingPlanId || '';
        sellingPlanInput.disabled = false;
        sellingPlanInput.value = planId;
      } else {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }
    }

    const addToCartButton = this.querySelector('.button');
    const buttonTextContent = addToCartButton?.querySelector('.add-to-cart-text__content');
    if (buttonTextContent) {
      if (mode === 'subscription') {
        buttonTextContent.textContent = 'SUBSCRIBE TODAY';
      } else {
        buttonTextContent.textContent = Theme.translations.add_to_cart || 'Add to cart';
      }
    }
  }

  #handleSubscriptionUpdate = (event) => {
    if (!this.#hasStickyProductForm()) return;

    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;
    if (this.dataset.productId && detail.productId && detail.productId !== this.dataset.productId) return;

    // Price is handled automatically by <product-price> listening to subscription:update.
    // Only sync selling plan input and button text here.
    this.#syncSubscriptionState(detail);
  };

  /**
   * Syncs non-price subscription state: selling plan input and button text.
   * @param {Record<string, any>} detail
   */
  #syncSubscriptionState(detail) {
    const sellingPlanInput = this.querySelector('[data-sticky-selling-plan]');
    if (sellingPlanInput instanceof HTMLInputElement) {
      if (detail.mode === 'subscription' && detail.planId) {
        sellingPlanInput.disabled = false;
        sellingPlanInput.value = detail.planId;
      } else {
        sellingPlanInput.value = '';
        sellingPlanInput.disabled = true;
      }
    }

    const addToCartButton = this.querySelector('.button');
    const buttonTextContent = addToCartButton?.querySelector('.add-to-cart-text__content');
    if (buttonTextContent) {
      if (detail.mode === 'subscription') {
        buttonTextContent.textContent = 'SUBSCRIBE TODAY';
      } else {
        buttonTextContent.textContent = Theme.translations.add_to_cart || 'Add to cart';
      }
    }
  }

  /**
   * @returns {HTMLInputElement | null}
   */
  #getMainQuantityInput() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const inputs = Array.from(
      section.querySelectorAll('quantity-selector-component input[ref="quantityInput"]')
    );

    for (const input of inputs) {
      if (this.contains(input)) continue;
      const form = input.closest('product-form-component');
      if (
        form instanceof HTMLElement &&
        this.dataset.productId &&
        form.dataset.productId !== this.dataset.productId
      ) {
        continue;
      }
      return input;
    }

    return null;
  }

  #getMainVariantId() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const inputs = Array.from(section.querySelectorAll('product-form-component input[name="id"]'));
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement)) continue;
      if (this.contains(input)) continue;
      const form = input.closest('product-form-component');
      if (
        form instanceof HTMLElement &&
        this.dataset.productId &&
        form.dataset.productId !== this.dataset.productId
      ) {
        continue;
      }
      return input.value;
    }

    return null;
  }

  /** @type {boolean} */
  #mainAtcInView = false;
  /** @type {boolean} */
  #mainAtcPassed = false;
  /** @type {boolean} */
  #footerInView = false;

  #handleViewportResize = () => {
    this.#refreshVisibilityState();
  };

  #setupVisibilityObserver() {
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.hidden = false;
    this.#mainAtcInView = false;
    this.#mainAtcPassed = false;
    this.#footerInView = false;

    const mainPrimaryAction = this.#getMainPrimaryAction();
    const footer = document.querySelector('footer, #shopify-section-footer, [id*="footer"]');

    if (!mainPrimaryAction && !footer) {
      this.#setVisible(false);
      return;
    }

    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.target === mainPrimaryAction) {
            this.#mainAtcInView = entry.isIntersecting;
            this.#mainAtcPassed = entry.boundingClientRect.bottom <= 0;
          } else {
            this.#footerInView = entry.isIntersecting;
          }
        }
        this.#setVisible(this.#shouldBeVisible());
      },
      { threshold: 0 }
    );

    if (mainPrimaryAction) this.#visibilityObserver.observe(mainPrimaryAction);
    if (footer) this.#visibilityObserver.observe(footer);

    this.#refreshVisibilityState();
  }

  /**
   * @param {boolean} visible
   */
  #setVisible(visible) {
    this.dataset.visible = visible ? 'true' : 'false';
    this.setAttribute('aria-hidden', visible ? 'false' : 'true');
    this.toggleAttribute('inert', !visible);
  }

  /**
   * @returns {boolean}
   */
  #shouldBeVisible() {
    if (window.innerWidth >= 750) {
      return this.#mainAtcPassed && !this.#footerInView;
    }

    return !this.#mainAtcInView && !this.#footerInView;
  }

  #refreshVisibilityState() {
    const mainPrimaryAction = this.#getMainPrimaryAction();
    const footer = document.querySelector('footer, #shopify-section-footer, [id*="footer"]');

    if (!mainPrimaryAction && !footer) {
      this.#setVisible(false);
      return;
    }

    if (mainPrimaryAction) {
      const { top, bottom } = mainPrimaryAction.getBoundingClientRect();
      this.#mainAtcInView = bottom > 0 && top < window.innerHeight;
      this.#mainAtcPassed = bottom <= 0;
    }

    if (footer) {
      const { top, bottom } = footer.getBoundingClientRect();
      this.#footerInView = bottom > 0 && top < window.innerHeight;
    }

    this.#setVisible(this.#shouldBeVisible());
  }

  /**
   * @returns {boolean}
   */
  #hasStickyProductForm() {
    return this.querySelector('product-form-component') instanceof HTMLElement;
  }

  /**
   * @returns {HTMLElement | null}
   */
  #getMainPrimaryAction() {
    const section = this.closest('.shopify-section');
    if (!section) return null;

    const productForms = Array.from(section.querySelectorAll('product-form-component'));
    const mainForm = productForms.find((form) => {
      if (!(form instanceof HTMLElement)) return false;
      if (this.contains(form)) return false;
      if (this.dataset.productId && form.dataset.productId && form.dataset.productId !== this.dataset.productId) {
        return false;
      }
      return true;
    });

    if (!mainForm) return null;

    const mainPrimaryPurchaseAction = mainForm.querySelector('[data-primary-purchase-actions]');
    if (mainPrimaryPurchaseAction instanceof HTMLElement) {
      return mainPrimaryPurchaseAction;
    }

    const addToCartButton = /** @type {HTMLElement | null} */ (
      mainForm.querySelector('add-to-cart-component [ref="addToCartButton"], add-to-cart-component .button, button[name="add"]')
    );

    if (addToCartButton) return addToCartButton;

    const externalBuyButton = /** @type {HTMLElement | null} */ (
      mainForm.querySelector('.external-buy-buttons a.external-buy-button, .external-buy-buttons .external-buy-button')
    );

    if (externalBuyButton) return externalBuyButton;

    const primaryPurchaseActions = Array.from(section.querySelectorAll('[data-primary-purchase-actions]'));
    return (
      primaryPurchaseActions.find((node) => {
        return node instanceof HTMLElement && !this.contains(node);
      }) || null
    );
  }

}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
